/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import { checkIfUserApplicationHasErrors } from "Models/BlinkApplication";
import useEventRouting from "hooks/useEventRouting";
import ROUTES from "routes";
import { useInvitation } from "state/InvitationContext";
import { useUser } from "state/UserContext";
import LOCAL_STORAGE_KEYS from "constants/STORAGE_KEYS";
import useLocalStorage from "utils/useLocalStorage";

const useRouting = () => {
  const { isFormCompleted } = useUser();
  const [id] = useLocalStorage(LOCAL_STORAGE_KEYS.id, null);
  const { navigateWithEvent } = useEventRouting();
  const { attendees, refetchAttendees } = useInvitation();
  const shouldEnterApplicationForm = (user) => {
    const { is_personal_form_completed } = user;
    if (is_personal_form_completed === false) return;
    if (isFormCompleted === true) {
      if (checkIfUserApplicationHasErrors(user)) return;
      navigateWithEvent(ROUTES.dashboard.base);
    } else {
      navigateWithEvent(ROUTES.onboard.registration);
    }
  };
  const shouldEnterInvitationForm = async (invitation) => {
    if (!invitation) return navigateWithEvent(ROUTES.base);
    const { status } = invitation;
    if (status === "approved") {
      const goto = await shouldEnterDonePage();
      return navigateWithEvent(goto);
    }
    if (status === "denied") {
      return navigateWithEvent(ROUTES.onboard.exit);
    }
  };
  const shouldApproveInvitation = async (invitation, is_form_completed) => {
    // if finished onboarding
    const _isFormCompleted =
      is_form_completed !== undefined ? is_form_completed : isFormCompleted;
    if (_isFormCompleted === true) {
      return ROUTES.dashboard.base;
    }
    // if has invitation
    if (invitation && invitation?.hasOwnProperty("id")) {
      // if the invitation is not approved, then the user should be redirected to sorry page.
      if (invitation.status === "denied") {
        return ROUTES.onboard.exit;
      }
      // if approved, go to companions
      if (invitation.status === "approved") {
        return await shouldEnterDonePage();
      }
      // if not, then approve.
      return ROUTES.onboard.invitation;
    } else {
      // if no invitation, go to form.
      return ROUTES.onboard.personalInfo(id);
    }
  };
  const shouldEnterCompanionsPage = () => {
    if (isFormCompleted === true) {
      return navigateWithEvent(ROUTES.dashboard.base);
    }
  };
  const findMainUser = (companionsWithGuest) =>
    companionsWithGuest?.find(
      (attendee) =>
        attendee?.id === id ||
        attendee?.hash_id === id ||
        attendee?.role === "user",
    );

  // Attendees are shaped as [mainUser, ...companions, ...emptyCompanionSlots],
  // so a single entry means the user has no companions at all.
  const hasOnlyMainUser = (companionsWithGuest) =>
    Array.isArray(companionsWithGuest) && companionsWithGuest.length === 1;

  /**
   * Where to send a user who would otherwise land on the "select guest"
   * (registration) page. When the user has no companions there is nothing to
   * select, so that screen is skipped: go to the done page if their personal
   * form is complete, otherwise straight into their personal form (BG-22958).
   */
  const resolveGuestSelectionRoute = (companionsWithGuest) => {
    const mainUser = findMainUser(companionsWithGuest);
    // if the main user is completed, go to the done page
    if (mainUser?.is_personal_form_completed === true) {
      return ROUTES.onboard.done;
    }
    if (hasOnlyMainUser(companionsWithGuest)) {
      return ROUTES.onboard.personalInfo(
        mainUser?.hash_id || mainUser?.id || id,
      );
    }
    // otherwise, go to the registration (select guest) page
    return ROUTES.onboard.registration;
  };

  const shouldEnterDonePage = async (newCompanions) => {
    let companionsWithGuest = newCompanions || attendees;
    if (!companionsWithGuest) {
      companionsWithGuest = await refetchAttendees();
    }
    // if the form is completed, go to the dashboard page
    if (isFormCompleted) {
      return ROUTES.dashboard.base;
    }
    return resolveGuestSelectionRoute(companionsWithGuest);
  };

  const logoRoute = isFormCompleted
    ? ROUTES.dashboard.base
    : ROUTES.onboard.registration;

  return {
    shouldEnterDonePage,
    resolveGuestSelectionRoute,
    shouldEnterApplicationForm,
    shouldApproveInvitation,
    shouldEnterInvitationForm,
    shouldEnterCompanionsPage,
    logoRoute,
  };
};

export default useRouting;
